import { Helmet } from 'react-helmet-async';

const SITE_URL = 'https://smartposafrica.com';
const SITE_NAME = 'Smart POS Africa';
const DEFAULT_IMAGE = `${SITE_URL}/images/og-image.png`;

const SEO = ({
  title,
  description,
  path = '/',
  image = DEFAULT_IMAGE,
  type = 'website',
  keywords = '',
  jsonLd = null,
  geoRegion = null,
  geoPlacename = null,
}) => {
  const fullTitle = title === 'Home'
    ? `POS Software for Retail, Restaurants & Service Businesses – ${SITE_NAME}`
    : `${title} – ${SITE_NAME}`;
  const url = `${SITE_URL}${path}`;

  const jsonLdArray = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];

  return (
    <Helmet>
      {/* Primary */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={url} />

      {/* Geographic targeting */}
      {geoRegion && <meta name="geo.region" content={geoRegion} />}
      {geoPlacename && <meta name="geo.placename" content={geoPlacename} />}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={`${SITE_NAME} – POS Software for African Businesses`} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="en_US" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* JSON-LD Structured Data */}
      {jsonLdArray.map((ld, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(ld)}
        </script>
      ))}
    </Helmet>
  );
};

// Pre-built JSON-LD for the software product
export const softwareJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: 'Smart POS Africa',
  applicationCategory: 'BusinessApplication',
  operatingSystem: 'Web, Android, iOS',
  description: 'All-in-one POS software and business management platform built for African businesses. Point of sale, inventory, staff management, expenses, and real-time analytics.',
  url: SITE_URL,
  offers: {
    '@type': 'Offer',
    price: '0',
    priceCurrency: 'GHS',
    description: 'One month free trial, then from GHS 300/month',
  },
  featureList: [
    'Point of Sale',
    'Inventory Management',
    'Staff Management',
    'Expense Tracking',
    'Real-time Analytics',
    'Multi-branch Support',
    'Offline Mode',
    'Mobile Money Payments',
    'Receipt Printing',
    'Customer Management',
  ],
  screenshot: `${SITE_URL}/images/screenshots/dashboard.png`,
  author: {
    '@type': 'Organization',
    name: 'Smart POS Africa',
    url: SITE_URL,
  },
};

// Organization JSON-LD
export const organizationJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Smart POS Africa',
  url: SITE_URL,
  logo: `${SITE_URL}/logo512.png`,
  description: 'Building the POS software and business tools African entrepreneurs need to compete and thrive.',
  sameAs: [],
};

// FAQ JSON-LD builder
export const buildFaqJsonLd = (faqs) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map(({ question, answer }) => ({
    '@type': 'Question',
    name: question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: answer,
    },
  })),
});

export default SEO;
